import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "../lib/auth";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import Logo from "../components/Logo";

export default function Register() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState("acme");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(email, name, password, tenantSlug);
      toast.success(`Welcome, ${data.user.name}`);
      nav(homeForRole(data.user.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden on-dark"
        style={{ background: "linear-gradient(135deg, #1A1743 0%, #373281 55%, #221D53 100%)" }}
      >
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full opacity-30 pointer-events-none blur-3xl"
             style={{ background: "radial-gradient(circle, #E4943A 0%, transparent 70%)" }}/>
        <div className="relative z-10">
          <Logo variant="light" size={40}/>
        </div>
        <div className="relative z-10 text-white max-w-md">
          <div className="text-[10px] font-mono tracking-widest uppercase mb-4" style={{ color: "#E4943A" }}>Join your workspace</div>
          <div className="font-display text-4xl leading-tight font-semibold text-white">
            Register with your company&apos;s PropHR workspace.
          </div>
          <div className="mt-6 text-sm text-[color:var(--fg-on-dark-muted)] leading-relaxed">
            Ask your HR admin for your tenant slug, or use <span className="font-mono text-white bg-white/10 px-1.5 py-0.5 rounded">acme</span> for the demo tenant.
          </div>
          <div className="mt-8 flex flex-wrap gap-4 text-xs font-mono uppercase tracking-widest text-[color:var(--fg-on-dark-muted)]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} strokeWidth={2}/> SOC 2</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} strokeWidth={2}/> GDPR</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} strokeWidth={2}/> CCPA</span>
          </div>
        </div>
        <div className="relative z-10 text-[10px] font-mono tracking-widest uppercase text-[color:var(--fg-on-dark-muted)]">
          © 2026 PropHR, Inc.
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-6 py-12 bg-white on-light">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo variant="dark" size={32}/>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">Create Account</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
            Get started with PropHR
          </h1>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div>
              <label className="eyebrow block mb-2">Full name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="Jane Doe"
                data-testid={TID.registerName}
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Work email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="jane@company.com"
                data-testid={TID.registerEmail}
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="At least 8 characters"
                data-testid={TID.registerPassword}
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Company Tenant Slug</label>
              <input
                required
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 font-mono"
                placeholder="e.g. acme"
                data-testid={TID.registerTenant}
              />
              <div className="text-xs text-slate-500 mt-1">Try <span className="font-mono text-slate-900">acme</span> for the demo workspace.</div>
            </div>
            <button className="btn-primary w-full py-3" disabled={loading} data-testid={TID.registerSubmit}>
              {loading ? "Creating…" : "Create account"} <ArrowRight size={14} strokeWidth={2}/>
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-brand hover:underline font-semibold">
              Sign in
            </Link>
          </div>
          <div className="mt-6 text-[10px] text-slate-400 font-mono leading-relaxed">
            BY REGISTERING YOU CONFIRM YOUR EMPLOYER USES PROPHR AS ITS AUTHORIZED HR CASE PLATFORM. FRAUDULENT REGISTRATIONS ARE INVESTIGATED AND MAY BE REPORTED TO THE APPROPRIATE AUTHORITIES.
          </div>
        </div>
      </div>
    </div>
  );
}
